import apiClient from './api';
import { API_ENDPOINTS } from '@/config/api.config';
import type {
  UserAchievementListRequest,
  UserAchievementListResponse,
  UserAchievementMetrics,
  UserAchievementDetails,
  UserAchievementDetailsRequest,
  APIResponse
} from '@/types/user-achievements.types';

class UserAchievementsService {
  /**
   * List all users achievements with pagination and sorting
   * Endpoint: POST /api/user-achievements/list
   */
  async listUserAchievements(params: UserAchievementListRequest): Promise<UserAchievementListResponse> {
    try {
      const payload = {
        page: params.page || 1,
        limit: params.limit || 20,
        sort_by: params.sort_by || 'xp',
        sort_order: params.sort_order || 'desc',
        search: params.search || undefined
      };

      const response = await apiClient.post<APIResponse<UserAchievementListResponse>>(
        API_ENDPOINTS.USER_ACHIEVEMENTS.LIST,
        payload
      );

      if (response.data?.success && response.data?.data) {
        return response.data.data;
      }

      return {
        data: [],
        total: 0,
        page: payload.page,
        limit: payload.limit,
        summary: {
          total_users: 0,
          total_xp: 0,
          total_badges: 0,
          total_certificates: 0
        }
      };
    } catch (error) {
      console.error('Error listing user achievements:', error);
      throw error;
    }
  }

  /**
   * Get achievement metrics
   * Endpoint: GET /api/user-achievements/metrics
   */
  async getMetrics(): Promise<UserAchievementMetrics> {
    try {
      const response = await apiClient.get<APIResponse<UserAchievementMetrics>>(
        API_ENDPOINTS.USER_ACHIEVEMENTS.METRICS
      );

      if (response.data?.success && response.data?.data) {
        return response.data.data;
      }

      return {
        total_users: 0,
        total_xp: 0,
        total_badges: 0,
        total_certificates: 0
      };
    } catch (error) {
      console.error('Error fetching achievement metrics:', error);
      return {
        total_users: 0,
        total_xp: 0,
        total_badges: 0,
        total_certificates: 0
      };
    }
  }

  /**
   * Get user achievement details
   * Endpoint: POST /api/user-achievements/details
   */
  async getUserDetails(params: UserAchievementDetailsRequest): Promise<UserAchievementDetails | null> {
    try {
      const payload: { user_id: string; search?: string; search_type?: string } = {
        user_id: params.user_id
      };

      if (params.search?.trim()) {
        payload.search = params.search.trim();
        if (params.search_type) {
          payload.search_type = params.search_type;
        }
      }

      const response = await apiClient.post<APIResponse<UserAchievementDetails>>(
        API_ENDPOINTS.USER_ACHIEVEMENTS.DETAILS,
        payload
      );

      if (response.data?.success && response.data?.data) {
        return response.data.data;
      }

      return null;
    } catch (error) {
      console.error('Error fetching user achievement details:', error);
      throw error;
    }
  }
}

export const userAchievementsService = new UserAchievementsService();
